/**
 * glTF JSON pointers on loaded Babylon nodes — addressing a loaded file's
 * nodes by INDEX, never by name.
 *
 * Babylon's glTF loader records, on every node it creates, the JSON pointers
 * of the glTF objects that node came from: `GLTFLoader.AddPointerMetadata`
 * pushes `"/nodes/{i}"` onto `node._internalMetadata.gltf.pointers` (and
 * `"/meshes/{m}/primitives/{p}"` onto a primitive's mesh). Those are the
 * file's own, stable indices. `TRACE_animation_binding` resolves a name to an
 * index in the file (`loaders/formats/gltf21/animationBinding`) and finds the
 * instance's node for that index through these pointers
 * (`loaders/animationBinding`) — never by a Babylon node name.
 *
 * The one gap is the container cache. `AssetContainer.instantiateModelsToScene`
 * clones the container's nodes, and only `Mesh.clone` carries
 * `_internalMetadata` across (Babylon 9.x `mesh.pure.js`); a `TransformNode`
 * clone goes through `SerializationHelper.Clone`, which copies decorated
 * properties only, and loses the pointers. {@link instantiateWithGltfPointers}
 * closes that gap at the one place the viewer instantiates from a container
 * (`ModelLoader`), so every loaded
 * instance answers the same question the same way whichever load path
 * produced it.
 */

import type { AssetContainer, InstantiatedEntries } from "@babylonjs/core/assetContainer";
import type { Node } from "@babylonjs/core/node";

/** The shape Babylon's glTF loader writes (`GLTFLoader.AddPointerMetadata`). */
interface GltfInternalMetadata {
    gltf?: { pointers?: string[] };
}

type WithInternalMetadata = { _internalMetadata?: GltfInternalMetadata };

/** The glTF JSON pointers a loaded node came from — empty when it has none. */
export function getGltfPointers(node: Node): readonly string[] {
    return (node as unknown as WithInternalMetadata)._internalMetadata?.gltf?.pointers ?? [];
}

/**
 * `container.instantiateModelsToScene(...)`, with every produced node carrying
 * the glTF pointers of the container node it was made from.
 *
 * Pairing is by identity, never by name or order:
 *
 *  - a `TransformNode`/`Mesh` clone receives its source's `metadata` BY
 *    REFERENCE (`SerializationHelper.Clone` copies the field as-is, and
 *    `Mesh.clone` assigns it). So for the duration of the call each
 *    pointer-carrying source gets a private SENTINEL object as its metadata;
 *    a clone holding a sentinel names its source;
 *  - an instance made straight from a container mesh names it as its
 *    `sourceMesh`.
 *
 * Afterwards every source gets its own metadata back, and every clone gets
 * the source's original `metadata` value — null when it had none, and a
 * shallow copy of a plain object (the glTF loader gives every node `{}`),
 * never the shared object itself. The sentinels never escape, and no two
 * placements share a metadata object.
 * A clone that already carries pointers (`Mesh.clone` copies them) is left
 * alone.
 */
/**
 * A clone's own metadata: a shallow copy of a plain-object source value, so
 * a per-placement write (`Renderer` sets `metadata.castShadows`) cannot
 * reach the other placements. Babylon would hand every clone the SAME object
 * (the glTF loader gives every node one), which no placement expects.
 */
function ownCopy(original: unknown): unknown {
    if (original && typeof original === "object" && Object.getPrototypeOf(original) === Object.prototype) {
        return { ...(original as Record<string, unknown>) };
    }
    return original;
}

export function instantiateWithGltfPointers(
    container: AssetContainer,
    ...args: Parameters<AssetContainer["instantiateModelsToScene"]>
): InstantiatedEntries {
    const sentinels = new Map<object, { source: Node; original: unknown }>();
    const sources = [...container.transformNodes, ...container.meshes] as Node[];
    for (const source of sources) {
        if (getGltfPointers(source).length === 0) continue;
        const sentinel = Object.freeze({ __gltfPointerCarry: true });
        sentinels.set(sentinel, { source, original: source.metadata });
        source.metadata = sentinel;
    }

    let entries: InstantiatedEntries;
    try {
        entries = container.instantiateModelsToScene(...args);
    } finally {
        for (const { source, original } of sentinels.values()) source.metadata = original;
    }

    const containerNodes = new Set<Node>(sources);
    const visit = (node: Node): void => {
        const held = node.metadata as object | null | undefined;
        const pairing = held && typeof held === "object" ? sentinels.get(held) : undefined;
        if (pairing) node.metadata = ownCopy(pairing.original);

        const target = node as unknown as WithInternalMetadata;
        if (getGltfPointers(node).length === 0) {
            let source: Node | undefined = pairing?.source;
            if (!source && node.getClassName() === "InstancedMesh") {
                const sourceMesh = (node as unknown as { sourceMesh?: Node }).sourceMesh;
                if (sourceMesh && containerNodes.has(sourceMesh)) source = sourceMesh;
            }
            if (source && source !== node) {
                // Shared, as `Mesh.clone` shares it: pointers are the file's
                // facts, identical for every instance of it.
                target._internalMetadata = (source as unknown as WithInternalMetadata)._internalMetadata;
            }
        }
        for (const child of node.getChildren()) visit(child);
    };
    for (const root of entries.rootNodes) visit(root);
    return entries;
}
